import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import toastService from '@/services/toast/service'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  trackingAdminService,
  PLATFORM_FUNNELS,
  PLATFORM_EVENTS,
  type AdminPixel,
  type AdminPixelInput,
  type PlatformFunnel,
  type PlatformCapiEvent,
} from '@/services/trackingAdmin'

interface PixelEditModalProps {
  open: boolean
  pixel: AdminPixel | null
  onOpenChange: (open: boolean) => void
  onSaved: (pixel: AdminPixel) => void
}

interface Draft {
  label: string
  pixelId: string
  accessToken: string
  testEventCode: string
  enabled: boolean
  funnels: PlatformFunnel[]
  events: PlatformCapiEvent[]
}

const draftFrom = (pixel: AdminPixel | null): Draft => ({
  label: pixel?.label ?? '',
  pixelId: pixel?.pixelId ?? '',
  accessToken: '',
  testEventCode: pixel?.testEventCode ?? '',
  enabled: pixel?.enabled ?? true,
  funnels: pixel?.funnels ?? ['signup', 'register'],
  events: pixel?.events ?? ['Lead', 'StartTrial', 'Subscribe'],
})

const toggle = <T,>(list: T[], value: T, on: boolean): T[] =>
  on ? (list.includes(value) ? list : [...list, value]) : list.filter((v) => v !== value)

export default function PixelEditModal({ open, pixel, onOpenChange, onSaved }: PixelEditModalProps) {
  const [draft, setDraft] = useState<Draft>(draftFrom(pixel))
  const [saving, setSaving] = useState(false)
  const isEdit = pixel !== null

  useEffect(() => {
    if (open) setDraft(draftFrom(pixel))
  }, [open, pixel])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    const body: AdminPixelInput = {
      label: draft.label.trim(),
      pixelId: draft.pixelId.trim(),
      testEventCode: draft.testEventCode.trim() || null,
      enabled: draft.enabled,
      funnels: draft.funnels,
      events: draft.events,
    }
    if (draft.accessToken.trim()) body.accessToken = draft.accessToken.trim()
    setSaving(true)
    try {
      const saved = isEdit
        ? await trackingAdminService.updatePixel(pixel.id, body)
        : await trackingAdminService.createPixel(body)
      toastService.success(isEdit ? 'Pixel updated' : 'Pixel added')
      onSaved(saved)
    } catch (err: any) {
      toastService.error(err?.response?.data?.message || (isEdit ? "Couldn't update the pixel" : "Couldn't add the pixel"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit pixel' : 'Add pixel'}</DialogTitle>
            <DialogDescription>
              Pixel id and Conversions API access token from Meta Events Manager. The token is stored encrypted.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pixel-label">Label</Label>
              <Input id="pixel-label" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Signup funnel pixel" required maxLength={100} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pixel-id">Pixel ID</Label>
              <Input id="pixel-id" value={draft.pixelId} onChange={(e) => setDraft({ ...draft, pixelId: e.target.value })} placeholder="1106372231830792" required inputMode="numeric" pattern="\d{5,20}" className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pixel-token">Access token</Label>
              <Input
                id="pixel-token"
                type="password"
                autoComplete="off"
                value={draft.accessToken}
                onChange={(e) => setDraft({ ...draft, accessToken: e.target.value })}
                placeholder={isEdit ? 'Leave blank to keep the current token' : 'EAAB...'}
                required={!isEdit}
              />
              {isEdit && pixel.accessTokenLast4 && (
                <p className="text-xs text-muted-foreground">Current token ends in {pixel.accessTokenLast4}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pixel-test-code">Test event code</Label>
              <Input id="pixel-test-code" value={draft.testEventCode} onChange={(e) => setDraft({ ...draft, testEventCode: e.target.value })} placeholder="TEST12345" maxLength={50} />
              <p className="text-xs text-muted-foreground">
                While set, every send lands in Meta's Test Events tab instead of counting. Clear it to go live. Required for the Send test button.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Funnels</legend>
                {PLATFORM_FUNNELS.map((f) => (
                  <label key={f.value} className="flex items-start gap-2 text-sm">
                    <Checkbox checked={draft.funnels.includes(f.value)} onCheckedChange={(c) => setDraft({ ...draft, funnels: toggle(draft.funnels, f.value, c === true) })} />
                    <span>
                      <span className="block">{f.label}</span>
                      <span className="block text-xs text-muted-foreground">{f.hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Events</legend>
                {PLATFORM_EVENTS.map((ev) => (
                  <label key={ev.value} className="flex items-start gap-2 text-sm">
                    <Checkbox checked={draft.events.includes(ev.value)} onCheckedChange={(c) => setDraft({ ...draft, events: toggle(draft.events, ev.value, c === true) })} />
                    <span>
                      <span className="block">{ev.label}</span>
                      <span className="block text-xs text-muted-foreground">{ev.hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label htmlFor="pixel-enabled">Enabled</Label>
                <p className="text-xs text-muted-foreground">Disabled pixels are skipped without logging.</p>
              </div>
              <Switch id="pixel-enabled" checked={draft.enabled} onCheckedChange={(v) => setDraft({ ...draft, enabled: v })} />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isEdit ? 'Save changes' : 'Add pixel'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
